/**
 * DowngradeDialog Component
 *
 * This dialog is used in the PricingPage and is triggered when a user wants to
 * downgrade from a paid plan to the free plan. It shows the user what features
 * and limits will change with the downgrade.
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider,
  Alert,
  styled,
} from '@mui/material';
import { getPlan, PLAN_NAMES } from './plansConfig';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: '12px',
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: '32px 32px 16px 32px',
  textAlign: 'center',
}));

const MainTitle = styled('h2')(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  fontSize: '1.5rem',
  marginBottom: '8px',
  margin: 0,
}));

const SubTitle = styled('p')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '1rem',
  lineHeight: 1.5,
  margin: 0,
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: '0 32px',
}));

const StyledAlert = styled(Alert)(({ theme }) => ({
  marginBottom: '24px',
  backgroundColor: 'var(--status-amber-bg) !important',
  color: 'var(--status-amber-color)',
  border: '1px solid var(--status-amber-border)',
  borderRadius: '4px!important',
  boxShadow: 'none!important',
  '& .MuiAlert-icon': {
    color: theme.palette.warning.main,
  },
}));

const ChangesTitle = styled('h6')(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: '.875em!important',
  marginBottom: '24px',
  textAlign: 'center',
  margin: '0 0 24px 0',
  fontWeight: 'bold!important',
}));

const FeatureBox = styled(Grid)(({ theme }) => ({
  padding: '24px',
  backgroundColor: 'var(--status-red-bg)',
  borderRadius: '8px',
  border: '1px solid var(--status-red-border)',
}));

const FeatureTitle = styled('h4')(({ theme }) => ({
  color: 'var(--status-red-color)',
  marginBottom: '16px',
  fontSize: '0.875rem',
  fontWeight: 'bold!important',
  margin: '0 0 16px 0',
}));

const FeatureText = styled('p')(({ theme }) => ({
  fontSize: '0.8rem',
  color: 'var(--status-red-color)',
  marginBottom: '8px',
  margin: '0 0 8px 0',
  '&:last-child': {
    marginBottom: 0,
  },
}));

const FeatureTextGroup = styled('div')(({ theme }) => ({
  '& > *:last-child': {
    marginBottom: 0,
  },
}));

const SupportFeatureGroup = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: '32px',
}));

const SupportFeatureText = styled('p')(({ theme }) => ({
  fontSize: '0.8rem',
  color: 'var(--status-red-color)',
  margin: 0,
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: '24px 32px 32px 32px',
  gap: '16px',
}));

const CancelButton = styled(Button)(({ theme }) => ({
  flex: 1,
  padding: '8px 0',
  fontWeight: 500,
  fontSize: '0.875rem',
  borderRadius: '6px',
  textTransform: 'none',
  backgroundColor: 'transparent',
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.custom.border,
  },
}));

const ConfirmButton = styled(Button)(({ theme }) => ({
  flex: 1,
  padding: '8px 0',
  fontWeight: 500,
  fontSize: '0.875rem',
  borderRadius: '6px',
  textTransform: 'none',
  backgroundColor: theme.palette.error.main,
  color: '#ffffff',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: theme.palette.error.dark,
    boxShadow: 'none',
  },
}));

const DowngradeDialog = ({
  open,
  onClose,
  onConfirm,
  currentPlan = getPlan(PLAN_NAMES.PRO),
  targetPlan = getPlan(PLAN_NAMES.FREE),
  processing = false,
}) => {
  return (
    <StyledDialog open={open} onClose={processing ? undefined : onClose} maxWidth="md" fullWidth>
      <StyledDialogTitle>
        <MainTitle>Confirm Plan Downgrade</MainTitle>
        <SubTitle>
          You're about to downgrade from <strong>{currentPlan.name}</strong> to{' '}
          <strong>{targetPlan.name}</strong>
        </SubTitle>
      </StyledDialogTitle>

      <StyledDialogContent>
        {/* Warning Alert */}
        <StyledAlert severity="warning">
          Your downgrade will take effect at the end of your current billing cycle.
        </StyledAlert>

        <Divider sx={{ my: 3 }} />

        {/* What You'll Lose */}
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <ChangesTitle>Changes with your downgrade</ChangesTitle>
          </Grid>

          <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
            {/* Usage Limits */}
            <Grid item xs={6} sx={{ display: 'flex' }}>
              <FeatureBox item sx={{ flex: 1 }}>
                <FeatureTitle>Usage Limits</FeatureTitle>
                <FeatureTextGroup>
                  <FeatureText>
                    Actions: {currentPlan.actions} → {targetPlan.actions} per month
                  </FeatureText>
                  <FeatureText>
                    AI Credits: {currentPlan.aiCredits} → {targetPlan.aiCredits}
                  </FeatureText>
                  <FeatureText>
                    Users: {currentPlan.users} → {targetPlan.users}
                  </FeatureText>
                  <FeatureText>
                    Storage: {currentPlan.storage} → {targetPlan.storage}
                  </FeatureText>
                </FeatureTextGroup>
              </FeatureBox>
            </Grid>

            {/* Workflow Features */}
            <Grid item xs={6} sx={{ display: 'flex' }}>
              <FeatureBox item sx={{ flex: 1 }}>
                <FeatureTitle>Workflow Features</FeatureTitle>
                <FeatureTextGroup>
                  <FeatureText>
                    Workflows: {currentPlan.workflows} → {targetPlan.workflows}
                  </FeatureText>
                  <FeatureText>
                    Components: {currentPlan.components} → {targetPlan.components} per workflow
                  </FeatureText>
                  <FeatureText>
                    Task duration: {currentPlan.taskDuration} → {targetPlan.taskDuration}
                  </FeatureText>
                </FeatureTextGroup>
              </FeatureBox>
            </Grid>

            {/* Support & Features */}
            <Grid item xs={12}>
              <FeatureBox item>
                <FeatureTitle>Support & Advanced Features</FeatureTitle>
                <SupportFeatureGroup>
                  <SupportFeatureText>
                    {currentPlan.support} → {targetPlan.support}
                  </SupportFeatureText>
                  <SupportFeatureText>
                    {currentPlan.integrations} → {targetPlan.integrations}
                  </SupportFeatureText>
                </SupportFeatureGroup>
              </FeatureBox>
            </Grid>
          </Grid>
        </Grid>
      </StyledDialogContent>

      <StyledDialogActions>
        <CancelButton onClick={onClose} variant="outlined" disabled={processing}>
          Keep {currentPlan.name} Plan
        </CancelButton>
        <ConfirmButton onClick={onConfirm} variant="contained" disabled={processing}>
          {processing ? 'Processing...' : 'Confirm Downgrade'}
        </ConfirmButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default DowngradeDialog;
